import {
  MODEL_SCORE_PILLAR_LABELS,
  MODEL_SCORE_PILLARS,
} from "@/lib/scoring/modelScoreFields";
import type { FilterChip, Lookups } from "../../types";
import { buildRangeLabel } from "../../utils/buildRangeLabel";
import {
  hasComposite,
  hasCriteria,
  hasRange,
  hasTiers,
  type ModelScoreEntry,
  type ModelScoreSlice,
} from "./slice";

export const MODEL_SCORE_CHIP_ID_PREFIX = "modelScore-";

/** Resolves the family id in `chip.id` back out (used by chip enrichment). */
export function modelIdFromChipId(chipId: string): string {
  return chipId.slice(MODEL_SCORE_CHIP_ID_PREFIX.length);
}

function modelName(modelId: string, lookups?: Lookups): string {
  // Lookups are absent until the argo catalog loads; a model missing from a
  // loaded catalog (deleted/unpublished) falls back to its raw family id.
  if (lookups?.models === undefined) return "Loading...";
  const match = lookups.models.find((m) => String(m.id) === modelId);
  return match?.name ?? modelId;
}

function criteriaLabel(entry: ModelScoreEntry): string {
  const parts: string[] = [];
  if (hasTiers(entry)) parts.push(`Tier ${entry.tiers?.join(", ")}`);
  if (hasComposite(entry)) {
    parts.push(
      `Score ${buildRangeLabel(entry.composite?.min, entry.composite?.max)}`
    );
  }
  for (const pillar of MODEL_SCORE_PILLARS) {
    const range = entry[pillar];
    if (!hasRange(range)) continue;
    parts.push(
      `${MODEL_SCORE_PILLAR_LABELS[pillar]} ${buildRangeLabel(range?.min, range?.max)}`
    );
  }
  return parts.join(", ");
}

export function getChips(
  slice: ModelScoreSlice,
  setSlice: (slice: ModelScoreSlice) => void,
  lookups?: Lookups
): FilterChip[] {
  return slice.entries.filter(hasCriteria).map((entry) => ({
    id: `${MODEL_SCORE_CHIP_ID_PREFIX}${entry.modelId}`,
    label: `${modelName(entry.modelId, lookups)}: ${criteriaLabel(entry)}`,
    filterKey: "modelScore" as const,
    onRemove: () =>
      setSlice({
        entries: slice.entries.filter((e) => e.modelId !== entry.modelId),
      }),
  }));
}

/** Sidebar badge counts criteria, not selected models — a criteria-less
 * selection activates scoring but doesn't filter. */
export function getCount(slice: ModelScoreSlice): number {
  return slice.entries.reduce(
    (acc, entry) =>
      acc +
      (hasComposite(entry) ? 1 : 0) +
      (hasTiers(entry) ? 1 : 0) +
      MODEL_SCORE_PILLARS.filter((pillar) => hasRange(entry[pillar])).length,
    0
  );
}
